/**
 * InAppAnnouncementHost
 * =====================
 * The two IN-PRODUCT surfaces for an admin in-app announcement:
 *
 *   • a PILL in the bottom-right corner, on every page
 *   • a sticky BANNER across the top of the Overview landing page, with a
 *     countdown when the send carries an auto-dismiss
 *
 * Mounted ONCE in AppShell, next to AdminAnnouncementModal.
 *
 * 🔴 THIS HOST OWNS THE PRESENTATION OF IN-APP ANNOUNCEMENTS — the generic toast
 * in NotificationContext deliberately stands down for them. Before this existed
 * every announcement rendered as a top-right Radix toast; letting both run would
 * show the same message twice, in two different places.
 *
 * 🔴 IT DOES NOT USE THE RADIX TOAST VIEWPORT. That viewport is global, anchored
 * top-right, and shared by every toast in the app — moving it to the bottom right
 * would relocate save-confirmations, sync errors and everything else along with
 * it. The pill is its own overlay so the placement is scoped to this feature.
 *
 * Placement is NOT decided here: `resolveInAppPlacement` is the single source of
 * truth, so the pill and the banner can never disagree about whether a given
 * announcement should interrupt.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, Megaphone, X } from 'lucide-react';
import { NotificationContext } from '@/contexts/NotificationContext';
import { InAppNotificationDialog } from './InAppNotificationDialog';
import {
  InAppAnnouncement,
  InAppSeverity,
  SEVERITY_PRESENTATION,
  inAppAnnouncementOf,
  resolveInAppPlacement,
} from '@/lib/notifications/inAppNotification';

/** Icon per severity — shared with the dialog and the modal. */
const SEVERITY_ICON: Record<InAppSeverity, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

/**
 * The Overview landing page, and the only route the banner renders on.
 *
 * `/home` exactly — every deeper `/home/*` page is a working surface where a
 * full-width bar across the top would push real content down on every visit.
 */
export const OVERVIEW_PATH = '/home';

export function isOverviewRoute(pathname: string): boolean {
  return String(pathname || '').replace(/\/+$/, '') === OVERVIEW_PATH;
}

/** One announcement plus the bell row it came from. */
interface LiveAnnouncement {
  /** Notification row id — unique per recipient, so it is the dismissal key. */
  id: string;
  title: string;
  /** The flattened one-liner. NEVER `announcement.body`, which is Markdown source. */
  message: string;
  createdAt: string | null;
  announcement: InAppAnnouncement;
}

/**
 * Pull the announcements that should currently interrupt the user.
 *
 * Reads the SAME bell rows the dropdown renders, so there is no second fetch and
 * no second source of truth. Unread only: a row the user has already opened has
 * been seen, and re-popping it on the next page load would be nagging.
 */
function useLiveAnnouncements(dismissed: Set<string>): LiveAnnouncement[] {
  // Defensive, exactly like AdminAnnouncementModal: the /admin/* tree has no
  // NotificationProvider and this host is mounted in the shared AppShell.
  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications;

  return useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    const out: LiveAnnouncement[] = [];
    for (const n of notifications) {
      const row = n as unknown as Record<string, unknown>;
      const id = String(row.id || '');
      if (!id || dismissed.has(id)) continue;
      if (row.isRead === true) continue;
      const announcement = inAppAnnouncementOf(n);
      if (!announcement) continue;
      out.push({
        id,
        title: String(row.title || ''),
        message: String(row.message || ''),
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
        announcement,
      });
    }
    return out;
  }, [notifications, dismissed]);
}

/**
 * Seconds remaining before an auto-dismiss fires, or null when persistent.
 *
 * Anchored to a MOUNT-time start rather than to `sent_at`: the countdown is a
 * promise about how long this surface will stay on screen, and an announcement
 * that arrived while the tab was closed would otherwise render already elapsed.
 */
function useCountdown(seconds: number | null, onElapsed: () => void): number | null {
  const [remaining, setRemaining] = useState<number | null>(seconds);
  // Held in a ref so a re-render from the parent cannot restart the timer.
  const onElapsedRef = useRef(onElapsed);
  useEffect(() => {
    onElapsedRef.current = onElapsed;
  }, [onElapsed]);

  useEffect(() => {
    if (seconds === null) {
      setRemaining(null);
      return;
    }
    setRemaining(seconds);
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(0, seconds - Math.floor((Date.now() - startedAt) / 1000));
      setRemaining(left);
      if (left <= 0) {
        window.clearInterval(tick);
        onElapsedRef.current();
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [seconds]);

  return remaining;
}

// ─── Pill ────────────────────────────────────────────────────────────────────

/**
 * One bottom-right pill.
 *
 * Its own component so each pill owns its countdown — a shared timer would make
 * two announcements with different durations dismiss together.
 */
const AnnouncementPill: React.FC<{
  item: LiveAnnouncement;
  onDismiss: () => void;
  onOpen: () => void;
}> = ({ item, onDismiss, onOpen }) => {
  const severity = SEVERITY_PRESENTATION[item.announcement.severity];
  const Icon = SEVERITY_ICON[item.announcement.severity];
  useCountdown(item.announcement.autoDismissSeconds, onDismiss);

  return (
    <motion.div
      layout
      // Spring, not a linear slide: the pill should settle rather than arrive.
      initial={{ opacity: 0, y: 24, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.95, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.8 }}
      className="pointer-events-auto w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-2 border-white bg-white shadow-lg ring-1 ring-black/5"
      data-testid="in-app-announcement-pill"
      data-severity={item.announcement.severity}
    >
      <div className="flex items-start gap-3 p-3.5">
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${severity.iconWrapClass}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          data-testid="in-app-announcement-pill-open"
        >
          <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
          {item.message ? (
            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{item.message}</p>
          ) : null}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          data-testid="in-app-announcement-pill-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
};

// ─── Banner ──────────────────────────────────────────────────────────────────

/** The top sticky bar, with the countdown when one applies. */
const AnnouncementBanner: React.FC<{
  item: LiveAnnouncement;
  onDismiss: () => void;
  onOpen: () => void;
}> = ({ item, onDismiss, onOpen }) => {
  const severity = SEVERITY_PRESENTATION[item.announcement.severity];
  const Icon = SEVERITY_ICON[item.announcement.severity];
  const remaining = useCountdown(item.announcement.autoDismissSeconds, onDismiss);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className={`flex items-center gap-3 border-b px-4 py-2.5 ${severity.chipClass}`}
      data-testid="in-app-announcement-banner"
      data-severity={item.announcement.severity}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 truncate text-left"
        data-testid="in-app-announcement-banner-open"
      >
        <span className="text-xs font-semibold">{item.title}</span>
        {item.message ? <span className="ml-2 text-xs opacity-80">{item.message}</span> : null}
      </button>
      {remaining !== null ? (
        <span
          className="shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-medium tabular-nums"
          data-testid="in-app-announcement-banner-countdown"
        >
          {remaining}s
        </span>
      ) : null}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 transition-colors hover:bg-white/60"
        data-testid="in-app-announcement-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
};

// ─── Host ────────────────────────────────────────────────────────────────────

export const InAppAnnouncementHost: React.FC = () => {
  const location = useLocation();
  const notificationContext = useContext(NotificationContext);

  // Per-mount dismissals. Deliberately NOT persisted: dismissing the pill means
  // "not now", and the bell row is still there. Opening it marks it read, which
  // is the explicit action that stops it returning.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const [opened, setOpened] = useState<LiveAnnouncement | null>(null);
  const live = useLiveAnnouncements(dismissed);
  const onOverview = isOverviewRoute(location.pathname);

  // Re-evaluated on every render rather than memoised on the announcement: an
  // expiry that lapses while the tab sits open must stop showing it.
  const pills: LiveAnnouncement[] = [];
  const banners: LiveAnnouncement[] = [];
  for (const item of live) {
    const placement = resolveInAppPlacement(item.announcement);
    if (placement.pill) pills.push(item);
    if (placement.banner && onOverview) banners.push(item);
  }

  /**
   * Opening one marks it read — the same thing clicking it in the bell does, and
   * what keeps it from returning on the next page load.
   */
  const open = useCallback(
    (item: LiveAnnouncement) => {
      setOpened(item);
      dismiss(item.id);
      Promise.resolve(notificationContext?.markAsRead?.(item.id)).catch(() => {
        // A failed read-mark must not break the dialog the user just opened; the
        // row simply stays unread and can be dismissed again.
      });
    },
    [dismiss, notificationContext]
  );

  return (
    <>
      {/* Top sticky banner — Overview only. Flush under the header, matching the
          Trial / WABA / Meta banner strip rather than floating over the chrome. */}
      {banners.length > 0 && (
        <div
          className="fixed left-0 right-0 z-40 top-[56px] sm:top-[64px] md:top-[62px]"
          data-testid="in-app-announcement-banner-host"
        >
          <AnimatePresence initial={false}>
            {banners.map((item) => (
              <AnnouncementBanner
                key={item.id}
                item={item}
                onDismiss={() => dismiss(item.id)}
                onOpen={() => open(item)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Bottom-right pill stack. `pointer-events-none` on the container so the
          gap between pills does not swallow clicks on the page beneath. */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[90] flex flex-col items-end gap-2"
        data-testid="in-app-announcement-pill-host"
      >
        <AnimatePresence initial={false}>
          {pills.map((item) => (
            <AnnouncementPill
              key={item.id}
              item={item}
              onDismiss={() => dismiss(item.id)}
              onOpen={() => open(item)}
            />
          ))}
        </AnimatePresence>
      </div>

      <InAppNotificationDialog
        open={!!opened}
        onClose={() => setOpened(null)}
        title={opened?.title || ''}
        createdAt={opened?.createdAt}
        announcement={opened?.announcement ?? null}
      />
    </>
  );
};

export default InAppAnnouncementHost;
